<template>
  <a class="button popupContainer"
     v-if="value && value.url"
     :href="value.url"
     :title="value.name"
     :aria-label="value.name"
     :class="{ horizontal, disabled }"
     :style="value.cssStyle"
     target="_blank">
    <div ref="button" :tabindex="acceptFocus ? 0 : -1"
         class="button popupMenuButton"
         :aria-label="acceptFocus && value.name ? value.name + ', button' : null"
         :class="[value.itemType, { root: isRoot, selected: value.selected }]"
         @click.stop.prevent="clicked"
         @keydown="onKeyPress">
      <BrandIcon v-if="value.icon"
                 class="icon"
                 :name="value.icon"
                 :showFocus="showIconFocus" />
      <div class="buttonLabel">{{ value.name }}</div>
    </div>
  </a>
  <div v-else-if="value &&
         (value.options && value.options.optionsType === 'router') &&
         value.itemType === 'item_hybrid' &&
         value.menuItems && !isRoot"
       ref="button" :tabindex="acceptFocus ? 0 : -1"
       :aria-label="ariaLabel"
       class="button popupContainer"
       :class="{ root: isRoot, horizontal, disabled }"
       :style="value.cssStyle"
       @click.stop.prevent="clicked"
       @keypress="onKeyPress"
       @focus="onFocus">
    <PopupMenu class="subMenu"
               :isRoot="false"
               :useMenuOpenerButton="false"
               :class="{ root: isRoot }"
               :value="subMenuValue"
               :items="value.menuItems"
               ref="subMenu"
               @subMenuClose="onSubMenuClose"
               @change="itemSelected">
    </PopupMenu>
  </div>
  <router-link v-else-if="value &&
                 (value.options && value.options.optionsType === 'router') &&
                 value.itemType !== 'item_hybrid' &&
                 !isRoot"
               :to="value.options.route">
    <div ref="button" :tabindex="acceptFocus ? 0 : -1"
         :aria-label="acceptFocus && value.name ? value.name + ', button' : null"
         class="button popupMenuButton"
         :class="[value.itemType, { root: isRoot, selected: value.selected }]"
         :style="value.cssStyle"
         @click.stop.prevent="clicked"
         @keydown="onKeyPress">
      <BrandIcon v-if="value.icon"
                 class="icon"
                 :name="value.icon"
                 :showFocus="showIconFocus" />
      <div class="buttonLabel"><span>{{ value.name }}</span></div>
    </div>
  </router-link>
  <div v-else-if="value && value.menuItems && !isRoot"
       ref="button" :tabindex="acceptFocus ? 0 : -1" :aria-label="ariaLabel"
       class="button popupContainer"
       :class="{ root: isRoot, horizontal, disabled }"
       :style="value.cssStyle"
       @click.stop.prevent="clicked"
       @keypress="onKeyPress"
       @focus="onFocus">
    <PopupMenu class="subMenu"
               :isRoot="false"
               :useMenuOpenerButton="false"
               :class="{ root: isRoot }"
               :value="subMenuValue"
               :items="value.menuItems"
               ref="subMenu"
               @subMenuClose="onSubMenuClose"
               @change="itemSelected">
    </PopupMenu>
  </div>
  <div v-else-if="value"
       ref="button" :tabindex="acceptFocus ? 0 : -1"
       :aria-label="acceptFocus && value.name ? value.name + ', button' : null"
       class="button popupMenuButton"
       :class="[value.itemType, { root: isRoot, selected: value.selected, horizontal, disabled }]"
       :style="value.cssStyle"
       @click.stop.prevent="clicked"
       @keydown="onKeyPress">
    <slot></slot>
    <div class="checkMarkContainer"
         v-if="!isRoot && value.itemType !== 'action' && value.itemType !== 'text' && value.selected">
      <BrandIcon class="checkMark"
                 name="special/tick"
                 :heavy="true"
                 :showFocus="false" />
    </div>
    <BrandIcon v-if="icon || value.icon"
               class="icon"
               :name="icon || value.icon"
               :showFocus="showIconFocus" />
    <div class="textBefore" v-if="value.textBefore && !isRoot">{{ value.textBefore }}</div>
    <div class="buttonLabel" v-if="name || value.name" :title="name || value.name"
         :class="{ hasTextAfter: value.textAfter && !name }">
      <span>{{ name || value.name }}</span>
    </div>
    <div class="textAfter" v-if="value.textAfter && !isRoot">{{ value.textAfter }}</div>
    <BrandIcon v-if="isRoot && showChevron"
               class="icon chevron"
               :showFocus="false"
               name="special/chevron-down" />
    <div v-if="(value.itemType === 'menu' ||
           value.itemType === 'item_menu' || value.itemType === 'item_hybrid') && !isRoot"
         class="subMenuTrigger"
         :class="[value.itemType, isMobile]"
         title="Open sub-menu"
         @click.stop="emitSubMenuToggle">
      <BrandIcon v-if="(value.itemType === 'item_menu' || value.itemType === 'item_hybrid') && isMobile"
                 class="icon"
                 :showFocus="false"
                 name="solid/edit-solid" />
      <BrandIcon v-else-if="value.itemType === 'menu' ||
                   value.itemType === 'item_menu' || value.itemType === 'item_hybrid'" class="icon"
                 :showFocus="false"
                 name="special/chevron-right" />
    </div>
  </div>
  <div v-else ref="button" :tabindex="acceptFocus ? 0 : -1"
       :aria-label="ariaLabel"
       class="button popupMenuButton"
       :class="{ root: isRoot, disabled }"
       :style="value.cssStyle"
       @keydown="onKeyPress">
    <slot></slot>
    <PopupMenu class="subMenu"
               :isRoot="false"
               :class="{ root: isRoot }"
               ref="subMenu"
               @subMenuClose="onSubMenuClose"
               @change="itemSelected">
    </PopupMenu>
  </div>
</template>

<script setup lang="ts">
import { ComponentPublicInstance, computed, ComputedRef, defineAsyncComponent, Ref, ref } from 'vue';

import { PopupMenuApi } from '@/components/dialogs/popupmenu/PopupMenu.vue';
import { nonCircularCopy, PopupMenuItem } from '@/components/dialogs/popupmenu/PopupMenuItem';
import BrandIcon from '@/components/icons/BrandIcon.vue';
import { useRouter } from '@/composables/router/VueRouter';
import BrowserDetection from '@/data/BrowserDetection';

const PopupMenu = defineAsyncComponent(() => import('@/components/dialogs/popupmenu/PopupMenu.vue'));

const router = useRouter();

interface Props {
  value: PopupMenuItem;
  name?: string;
  icon?: string;
  // Flag for option to show focus indication around icons as per BrandIcon.vue
  showIconFocus?: boolean;
  disabled?: boolean;
  isSubMenu?: boolean;
  isRoot?: boolean;
  showChevron?: boolean;
  acceptFocus?: boolean;
  // Flag for option to display menu items horizontally as opposed to the default vertical
  horizontal?: boolean;
  parentValue?: PopupMenuItem;
}
const props = withDefaults(defineProps<Props>(), {
  showIconFocus: false,
  disabled: false,
  isSubMenu: false,
  isRoot: false,
  showChevron: false,
  acceptFocus: true,
  horizontal: false,
});

interface Emits {
  (e: 'change', item: PopupMenuItem): void,
  (e: 'itemClicked', value: PopupMenuItem): void,
  (e: 'subMenuToggle'): void,
  (e: 'subMenuOpen'): void,
  (e: 'subMenuClose', closeAll: boolean): void,
}
const emit = defineEmits<Emits>();

const button: Ref<HTMLButtonElement | null> = ref(null);
const subMenu: Ref<PopupMenuApi | null> = ref(null);

function emitItemClicked(): PopupMenuItem {
  emitSubMenuClose();
  emit('itemClicked', props.value);
  return props.value;
}

function getValue(): PopupMenuItem {
  return props.value;
}

const subMenuValue: ComputedRef<PopupMenuItem | undefined> = computed(() => {
  return nonCircularCopy(props.value);
});

function focus(): void {
  button.value?.focus();
}

function blur(): void {
  button.value?.blur();
}

function emitSubMenuToggle(): void {
  // Toggled sub menu
  emit('subMenuToggle');
}

function emitSubMenuOpen(): void {
  // Opened sub menu
  emit('subMenuOpen');
}

function emitSubMenuClose(closeAll: boolean = true): boolean {
  // Closed sub menu
  emit('subMenuClose', closeAll);
  return closeAll;
}

const ariaLabel: ComputedRef<string | null> = computed(() => {
  return props.acceptFocus && props.value.name ? props.value.name : null;
});

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

function onSubMenuClose(closeAll: boolean = true): void {
  if (closeAll) {
    emitSubMenuClose();
  }
}

function onKeyPress(event: KeyboardEvent): void {
  if (props.value && !props.value.menuItems) {
    if (!props.horizontal) {
      switch (event.key) {
        case 'ArrowLeft' :
          event.stopImmediatePropagation();
          event.preventDefault();
          emitSubMenuClose(false);
          break;
        case 'ArrowRight' :
          event.stopImmediatePropagation();
          event.preventDefault();
          emitSubMenuOpen();
          break;
        case ' ' :
          event.stopImmediatePropagation();
          event.preventDefault();
          emitSubMenuToggle();
          break;
        case 'Enter' :
          event.stopImmediatePropagation();
          event.preventDefault();
          clicked();
          break;
      }
    } else {
      switch (event.key) {
        case 'ArrowDown' :
          event.stopImmediatePropagation();
          event.preventDefault();
          emitSubMenuClose(false);
          break;
        case 'ArrowUp' :
          event.stopImmediatePropagation();
          event.preventDefault();
          emitSubMenuOpen();
          break;
        case ' ' :
          event.stopImmediatePropagation();
          event.preventDefault();
          emitSubMenuToggle();
          break;
        case 'Enter' :
          event.stopImmediatePropagation();
          event.preventDefault();
          clicked();
          break;
      }
    }
  }
}

function onFocus(): void {
  subMenu.value?.focusMenuOpenerButton();
}

function clicked(): void {
  if (!props.disabled && props.value) {
    switch (props.value.itemType) {
      case 'item_menu' :
        if (props.isRoot) {
          emitSubMenuToggle();
        } else {
          emitSubMenuOpen();
        }
        break;
      case 'item' :
      case 'item_hybrid' :
        if (props.isRoot) {
          emitSubMenuToggle();
        } else {
          if (props.value.route) {
            router.push(props.value.route);
          } else if (props.value.callback) {
            props.value.callback(props.value);
          }
          emitItemClicked();
        }
        break;
      case 'menu' :
        if (props.value.route) {
          router.push(props.value.route);
        } else if (props.value.callback) {
          props.value.callback(props.value);
        } else {
          emitSubMenuToggle();
        }
        break;
      case 'url' :
        window.open(props.value.url, '_blank');
        emitSubMenuClose();
        break;
      case 'action' :
        if (props.value.route) {
          router.push(props.value.route);
        } else if (props.value.callback) {
          props.value.callback(props.parentValue);
        }
        emitSubMenuClose();
        break;
      case 'in_menu_action' :
        if (props.value.callback) {
          props.value.callback(props.parentValue);
        }
        break;
    }
  } else {
    emitItemClicked();
  }
}

function itemSelected(item: PopupMenuItem): void {
  emit('change', item);
}

export interface PopupMenuButtonApi extends ComponentPublicInstance {
  focus: () => void;
  blur: () => void;
  getValue: () => PopupMenuItem;
}

defineExpose({
  focus,
  blur,
  getValue,
});
</script>

<style scoped lang="scss">
.menuOpener.root > button .buttonLabel {
  font-weight: $weight-bold;
}
a,
.button {
  min-height: 2rem;
  max-height: 2rem;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  &.popupMenuButton {
    position: relative;
    max-width: 100%;
    justify-content: flex-start;
    padding: 0 2rem 0 1rem;
    transition: background-color $transition-time-medium ease-in-out;
    cursor: pointer;
    &.horizontal, .horizontal.popupMenuContainer & {
      flex-direction: column;
      padding: 0.5rem;
      max-height: inherit;
    }
    &.disabled {
      opacity: 0.5;
    }
    &.action {
      padding-right: 1.5rem;
    }
    &.item_hybrid,
    &.item_menu,
    &.menu {
      padding-right: 0;
    }
    border-radius: 0;
    &.selected {
      .checkMarkContainer {
        .checkMark {
          visibility: visible;
          opacity: 1;
        }
      }
    }
    &.alert {
      .buttonLabel {
        color: $colour-alert;
      }
      &:hover {
        background: $colour-alert;
        .buttonLabel {
          color: white;
        }
      }
    }
    &.root {
      padding-right: 0.5rem;
    }
    &.menu_heading,
    &.text {
      pointer-events: none;
      cursor: default;
      .buttonLabel {
        font-weight: $weight-bold;
        max-height: 1rem;
        align-items: flex-end;
      }
    }
    &.item_hybrid,
    &.item_menu {
      .subMenuTrigger {
        visibility: hidden;
        opacity: 0;
        color: $colour-icon-menu-submenu;
        .mobile & {
          visibility: visible;
          opacity: 1;
        }
      }
    }
    .subMenuTrigger {
      display: flex;
      align-items: center;
      justify-content: center;
      right: 0;
      color: $colour-icon-menu-submenu;
      border: none;
      width: 1.75rem;
      height: 1.75rem;
      border-radius: 0;
      cursor: context-menu;
      transition: opacity 0.25s ease-in-out;
      &.right.menu {
        .brandIconContainer {
          transform: rotate(180deg);
          left: 0.125rem;
        }
      }
      .brandIconContainer {
        width: 1rem;
        height: 1rem;
      }
    }
    .chevron {
      color: $colour-icon-primary;
      width: 1rem;
      height: 1rem;
      margin-left: 0.75rem;
    }
    .checkMarkContainer {
      position: absolute;
      left: 0.125rem;
      .checkMark {
        left: 0.25rem;
        width: 1rem;
        height: 1rem;
        visibility: hidden;
        opacity: 0;
        transition: all 0.25s ease-in-out;
        color: $colour-link-emphasis;
        :deep(svg.icon.heavy) {
          stroke-width: 1px;
        }
      }
    }
    &:focus-visible {
      outline: none;
    }
    &:focus {
      outline: none;
      background: $colour-background-mid-light;
      &.item_hybrid,
      &.item_menu {
        .subMenuTrigger {
          visibility: visible;
          opacity: 1;
        }
      }
    }
    &:hover {
      background: $colour-background-mid-light;
      &.item_hybrid,
      &.item_menu {
        .subMenuTrigger {
          visibility: visible;
          opacity: 1;
        }
      }
    }
    &.root {
      &:focus {
        background: none;
      }
      &:hover {
        background: none;
      }
    }
    .textAfter {
      font-weight: $weight-normal;
      color: $colour-text-secondary-emphasis;
      font-style: normal;
      margin: 0 0.25rem;
      margin-right: 0.75rem;
      white-space: nowrap;
    }
    .textBefore {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1rem;
      margin: 0;
      min-width: 1.5rem;
      height: 1.5rem;
    }
    .buttonLabel {
      max-width: 100%;
      padding: 0 0.25rem 0 0;
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: flex-start;
      pointer-events: none;
      white-space: nowrap;
      overflow: hidden;
      font-weight: $weight-semi-bold;
      color: $colour-text-primary;
      height: 1.75rem;
      margin-left: 0.25rem;
      transition: color $transition-time-medium ease-in-out;
      &.hasTextAfter {
        margin-right: 0;
      }
      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}
.icon {
  .brandIconContainer {
    :deep(.disabled svg.icon) {
      color: currentColor;
    }
  }
  color: $colour-icon-menu-submenu;
  pointer-events: none;
}
</style>
